import { ModuleEnum } from '../enums/ModuleEnum';
import { DatabaseClientInterface } from '../sql/DatabaseClientInterface';
import { AttributesFilterInputInterface } from '../interfaces/AttributesFilterInputInterface';
import { LocalesEnum } from '../enums/LocalesEnum';
import { AttributeValueWithCountInterface } from '../interfaces/AttributeValueWithCountInterface';
import { AttributeUniqValuesInterface } from '../interfaces/AttributeUniqValuesInterface';
import { ATTRIBUTES } from '../constants';
import { AttributesClassInterface, ScalarProductAttributes } from '../interfaces/AttributesClassInterface';
import { ValveAccessoriesWithTypeInterface } from '../interfaces/ValveAccessoriesWithTypeInterface';
import { extractJsonFieldSql } from '../sql/extractJsonFieldSql';
import { ProductInterface } from '../interfaces/ProductInterface';
import { SensorAccessoriesInterface } from '../interfaces/SensorAccessoriesInterface';
import { buildWhereFromFilters } from '../sql/buildWhereFromFilters';
import { prepareValuesMapping } from '../sql/prepareValuesMapping';
import { buildWhereByModule } from '../sql/buildWhereByModule';
import { buildUniqueValuesSql } from '../sql/buildUniqueValuesSql';

export class SensorsResolver {
    constructor(
        private readonly db: DatabaseClientInterface,
        private readonly mapValues: (locale: LocalesEnum, rawName: LocalesEnum) => (arg: any) => AttributeValueWithCountInterface,
        private readonly mapToProduct: (row: any) => ProductInterface,
        protected readonly discontinuedAttr: string
    ) {
    }

    public async sensorsValues(
        id: string,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        locale: LocalesEnum,
        skipExclude?: boolean,
    ): Promise<AttributeUniqValuesInterface> {

        const {
            exclude,
            localized,
            attrValue
        } = prepareValuesMapping(id, rawName, [ATTRIBUTES.MEDIA]);

        const [whereModule, paramsModule] = buildWhereByModule(ModuleEnum.SensorSizer, rawName, this.discontinuedAttr);
        const [whereFilter, paramsFilter] = buildWhereFromFilters(filter, skipExclude ? []: exclude, rawName);
        const query = buildUniqueValuesSql(
            localized,
            attrValue,
            'product',
            `${whereModule} ${whereFilter.length > 0 ? ` AND ${whereFilter}` : ''}`
        );

        const values = await this.db.getMany(
            query,
            [...paramsModule, ...paramsFilter],
            this.mapValues(locale, rawName)
        );
        return { values };
    }

    public async sensorAccessories(
        rawName: LocalesEnum,
        sensorValve: string,
        filter: AttributesFilterInputInterface,
    ): Promise<SensorAccessoriesInterface> {
        const [sensor, sensorAccessories] = await this.getAccessories(
            sensorValve,
            rawName,
            [
                '_582',
                '_8012',
                '_7812',
                '_7793',
                '_3442',
            ],
            filter,
        );

            return {
                sensor,
                sensorAccessories,
            }
    }

    protected async getAccessories(
        label: string,
        rawName: LocalesEnum,
        accessoriesAttrs: Array<keyof Omit<AttributesClassInterface, keyof ScalarProductAttributes>>,
        filter: AttributesFilterInputInterface,
    ): Promise<[AttributesClassInterface, ValveAccessoriesWithTypeInterface[]]> {
        const product = await this.db.getOne(
            'SELECT id, label, attributes FROM product WHERE label = ?',
            [label],
            this.mapToProduct
        );
        if (!product) {
            throw new Error(`product not found - "${label}"`);
        }
        const attributes = product.getAttributes();
        const [where, params] = buildWhereFromFilters(filter, [], rawName);
        const accessoriesResolved =  await Promise.all(accessoriesAttrs.map(async (attr) => {
            const accessoriesLabels = attributes?.[attr]?.[rawName] ?? [];
            if (accessoriesLabels.length === 0) {
                return [];
            }
            const accessories = await this.db.getMany(
                `SELECT id, label, attributes
                        FROM product
                        WHERE label in (${accessoriesLabels.map(_ => '?').join(',')})
                     ${where.length > 0 ? ` AND ${where}` : ''}
                     ORDER BY ${extractJsonFieldSql('attributes', `$.8543.${rawName}`)} DESC, label 
                        `,
                [...accessoriesLabels, ...params],
                this.mapToProduct
            );

            return accessories.map<ValveAccessoriesWithTypeInterface>((item) => {
                return {
                    source: attr,
                    item: item.getAttributes(),
                }
            });
        }))

        const accessories = accessoriesResolved.reduce((acc, curr) => {
            return [...acc, ...curr];
        }, [] as ValveAccessoriesWithTypeInterface[]);

        return [
            attributes,
            accessories,
        ];
    }
}
